import type { KpiComparisonPeriod } from "@types";
import api, { v2BaseUrl } from "../api";
import { DashboardFilter } from "../../types/dashboard";
import { buildAssetTreePayload } from "@utils/assetTreePayload";

const assetTreeFrom = (filters?: DashboardFilter) =>
  buildAssetTreePayload({
    branchIds: filters?.branchIds,
    segmentIds: filters?.segmentIds,
    clientIds: filters?.clientIds,
    supplierIds: filters?.supplierIds,
    vehicleIds: filters?.vehicleIds,
    driverIds: filters?.driverIds,
    includeNullBranch: filters?.includeNullBranch,
    includeNullSegment: filters?.includeNullSegment,
    includeNullClient: filters?.includeNullClient,
    includeNullSupplier: filters?.includeNullSupplier,
    includeNullVehicle: filters?.includeNullVehicle,
    includeNullDriver: filters?.includeNullDriver,
  });

interface KpiComparisonPeriods {
  currentPeriod?: KpiComparisonPeriod | null;
  previousPeriod?: KpiComparisonPeriod | null;
}

export interface TollsSummaryMetric {
  value: number;
  previousValue: number;
  variationPercent: number;
  currentPeriod?: KpiComparisonPeriod | null;
  previousPeriod?: KpiComparisonPeriod | null;
}

export interface TollsSummary {
  totalSpent: TollsSummaryMetric;
  totalPassages: TollsSummaryMetric;
  totalVehicles: TollsSummaryMetric;
  averageCostPerPassage: TollsSummaryMetric;
  averageCostPerVehicle: TollsSummaryMetric;
}

const EMPTY_METRIC: TollsSummaryMetric = {
  value: 0,
  previousValue: 0,
  variationPercent: 0,
  currentPeriod: null,
  previousPeriod: null,
};

export const EMPTY_TOLLS_SUMMARY: TollsSummary = {
  totalSpent: EMPTY_METRIC,
  totalPassages: EMPTY_METRIC,
  totalVehicles: EMPTY_METRIC,
  averageCostPerPassage: EMPTY_METRIC,
  averageCostPerVehicle: EMPTY_METRIC,
};

const normalizeMetric = (metric: any, fallbackPeriods?: KpiComparisonPeriods): TollsSummaryMetric => ({
  value: Number(metric?.value ?? 0),
  previousValue: Number(metric?.previousValue ?? 0),
  variationPercent: Number(metric?.variationPercent ?? 0),
  currentPeriod: metric?.currentPeriod ?? fallbackPeriods?.currentPeriod ?? null,
  previousPeriod: metric?.previousPeriod ?? fallbackPeriods?.previousPeriod ?? null,
});

export const getTollsSummary = async (filters: DashboardFilter): Promise<TollsSummary> => {
  try {
    const body = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      assetTree: assetTreeFrom(filters),
    };

    const response = await api.post(`${v2BaseUrl}/tolls/summary`, body);
    const data = response.data?.data ?? response.data ?? {};
    const periods = { currentPeriod: data.currentPeriod, previousPeriod: data.previousPeriod };

    return {
      totalSpent: normalizeMetric(data.totalSpent, periods),
      totalPassages: normalizeMetric(data.totalPassages, periods),
      totalVehicles: normalizeMetric(data.totalVehicles, periods),
      averageCostPerPassage: normalizeMetric(data.averageCostPerPassage, periods),
      averageCostPerVehicle: normalizeMetric(data.averageCostPerVehicle, periods),
    };
  } catch (error) {
    console.error("Error fetching tolls summary:", error);
    return EMPTY_TOLLS_SUMMARY;
  }
};

export interface TollDailyExpense {
  date: string;
  amount: number;
  previousAmount: number;
}

export const getTollsDailyExpenses = async (filters: DashboardFilter): Promise<TollDailyExpense[]> => {
  try {
    const body = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      assetTree: assetTreeFrom(filters),
    };

    const response = await api.post(`${v2BaseUrl}/tolls/daily-expenses`, body);
    const data = response.data?.data ?? response.data ?? [];
    if (!Array.isArray(data)) return [];
    return data.map((item: any) => ({
      date: String(item?.date ?? ""),
      amount: Number(item?.amount ?? 0),
      previousAmount: Number(item?.previousAmount ?? 0),
    }));
  } catch (error) {
    console.error("Error fetching tolls daily expenses:", error);
    return [];
  }
};

export interface TopTollLocation {
  locationId: string;
  location: string;
  concessionaire: string;
  latitude: number;
  longitude: number;
  passages: number;
}

export const getTopTollLocations = async (
  filters: DashboardFilter,
  limit = 5,
  ascending = false
): Promise<TopTollLocation[]> => {
  try {
    const body = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      limit,
      ascending,
      assetTree: assetTreeFrom(filters),
    };

    const response = await api.post(`${v2BaseUrl}/tolls/top-locations`, body);
    const data = response.data?.data ?? response.data ?? [];
    if (!Array.isArray(data)) return [];
    return data.map((item: any) => ({
      locationId: String(item?.locationId ?? ""),
      location: String(item?.location ?? ""),
      concessionaire: String(item?.concessionaire ?? ""),
      latitude: Number(item?.latitude ?? 0),
      longitude: Number(item?.longitude ?? 0),
      passages: Number(item?.passages ?? 0),
    }));
  } catch (error) {
    console.error("Error fetching top toll locations:", error);
    return [];
  }
};

export interface TopTollConcessionaire {
  concessionaire: string;
  totalAmount: number;
}

export const getTopTollConcessionaires = async (
  filters: DashboardFilter,
  limit = 5,
  ascending = false
): Promise<TopTollConcessionaire[]> => {
  try {
    const body = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      limit,
      ascending,
      assetTree: assetTreeFrom(filters),
    };

    const response = await api.post(`${v2BaseUrl}/tolls/top-concessionaires`, body);
    const data = response.data?.data ?? response.data ?? [];
    if (!Array.isArray(data)) return [];
    return data.map((item: any) => ({
      concessionaire: String(item?.concessionaire ?? ""),
      totalAmount: Number(item?.totalAmount ?? 0),
    }));
  } catch (error) {
    console.error("Error fetching top toll concessionaires:", error);
    return [];
  }
};

export interface TollTransaction {
  transactionId: string;
  dateTime: string;
  vehicle: string;
  numberOfAxes: string;
  plaza: string;
  cityState: string;
  concessionaire: string;
  amount: number;
  payment: string;
}

export interface TollTransactionsPage {
  content: TollTransaction[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
}

const EMPTY_TOLLS_PAGE: TollTransactionsPage = {
  content: [],
  page: 0,
  size: 0,
  totalElements: 0,
  totalPages: 0,
  first: true,
  last: true,
};

export const getTolls = async (filters: DashboardFilter, page = 0, size = 20): Promise<TollTransactionsPage> => {
  try {
    const body = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      page,
      size,
      assetTree: assetTreeFrom(filters),
    };

    const response = await api.post(`${v2BaseUrl}/tolls`, body);
    const data = response.data?.data ?? response.data ?? {};
    const content = Array.isArray(data?.content) ? data.content : [];
    return {
      content: content.map((item: any) => ({
        transactionId: String(item?.transactionId ?? ""),
        dateTime: String(item?.startDateTime ?? item?.dateTime ?? ""),
        vehicle: String(item?.vehicle ?? ""),
        numberOfAxes: String(item?.numberOfAxes ?? ""),
        plaza: String(item?.plaza ?? ""),
        cityState: String(item?.cityState ?? ""),
        concessionaire: String(item?.concessionaire ?? ""),
        amount: Number(item?.amount ?? 0),
        payment: String(item?.payment ?? ""),
      })),
      page: Number(data?.page ?? 0),
      size: Number(data?.size ?? size),
      totalElements: Number(data?.totalElements ?? 0),
      totalPages: Number(data?.totalPages ?? 0),
      first: Boolean(data?.first ?? true),
      last: Boolean(data?.last ?? true),
    };
  } catch (error) {
    console.error("Error fetching tolls:", error);
    return EMPTY_TOLLS_PAGE;
  }
};

export interface TollDetail {
  transactionId: string;
  dateTime: string;
  vehicle: string;
  numberOfAxes: string;
  plaza: string;
  cityState: string;
  concessionaire: string;
  amount: number;
  payment: string;
}

export const getTollDetail = async (id: string): Promise<TollDetail | null> => {
  try {
    const response = await api.get(`/tolls/${id}`);
    const data = response.data?.data ?? response.data ?? null;
    if (!data) return null;
    return {
      transactionId: String(data?.transactionId ?? data?.id ?? id),
      dateTime: String(data?.startDateTime ?? data?.dateTime ?? ""),
      vehicle: String(data?.vehicle ?? ""),
      numberOfAxes: String(data?.numberOfAxes ?? ""),
      plaza: String(data?.plaza ?? ""),
      cityState: String(data?.cityState ?? ""),
      concessionaire: String(data?.concessionaire ?? ""),
      amount: Number(data?.amount ?? 0),
      payment: String(data?.payment ?? ""),
    };
  } catch (error) {
    console.error("Error fetching toll detail:", error);
    return null;
  }
};

export interface TollMapPin {
  plazaId: string;
  plazaName: string;
  plazaCity: string;
  plazaState: string;
  latitude: number;
  longitude: number;
  passages: number;
  lastPassageAt: string;
}

export const getTollMapPins = async (filters: DashboardFilter): Promise<TollMapPin[]> => {
  try {
    const body = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      assetTree: assetTreeFrom(filters),
    };

    const response = await api.post(`${v2BaseUrl}/tolls/map-pins`, body);
    const data = response.data?.data ?? response.data ?? [];
    if (!Array.isArray(data)) return [];
    return data
      .map((item: any) => ({
        plazaId: String(item?.plazaId ?? ""),
        plazaName: String(item?.plazaName ?? ""),
        plazaCity: String(item?.plazaCity ?? ""),
        plazaState: String(item?.plazaState ?? ""),
        latitude: Number(item?.latitude ?? 0),
        longitude: Number(item?.longitude ?? 0),
        passages: Number(item?.passages ?? 0),
        lastPassageAt: String(item?.lastPassageAt ?? ""),
      }))
      .filter((pin) => Number.isFinite(pin.latitude) && Number.isFinite(pin.longitude));
  } catch (error) {
    console.error("Error fetching toll map pins:", error);
    return [];
  }
};

export const downloadTollsXlsx = async (filters: DashboardFilter, page = 0, size = 100): Promise<void> => {
  const body = {
    startDate: filters.startDate,
    endDate: filters.endDate,
    page,
    size,
    assetTree: assetTreeFrom(filters),
  };

  try {
    const response = await api.post(`${v2BaseUrl}/tolls/export/xlsx`, body, { responseType: "blob" });

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `PEDAGIOS_${filters.startDate ?? ""}_${filters.endDate ?? ""}.xlsx`);

    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error exporting tolls:", error);
    throw error;
  }
};
